import { AxiosError } from "axios";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import { GetMarkets200Response, InstrumentTypeResponse } from "../../codegen-api";
import { WebsocketContext } from "../../contexts/WebsocketAuthContext";
import { APIEndpointEnum } from "../../enums/endpoint";
import { publicApi } from "../../services/api/apiFetcher";
import { forEachFrame, readFrame } from "./parseFrame";
import { IWSSMarkPriceResponse, IWSSMark } from "./model/markPrice";
import { WebsocketChannelEnum } from "./model/shared";
import { AssetResponse } from "../../utils/asset";
import { knownDisplayPrice } from "../../utils/markGuards";
import { apiNum } from "../../utils/math";

export type InstrumentToMark = {
  [key: string]: IWSSMark;
};

export type IMultiMarkPriceAssetDerivative = {
  [instrumentName: string]: {
    asset: AssetResponse;
    derivative: InstrumentTypeResponse;
  };
};

/**
 * When the `markprice` stream has said nothing about an instrument for this
 * long, its mark is taken from a polled /markets instead. Every consumer of
 * this hook — positions (PnL, ROE), the trade form (order value, margin,
 * liquidation estimate), close-position and TP/SL — otherwise froze on the
 * page-load mark whenever the socket was silent. That is not hypothetical: on
 * all three testnets /socket.io is served by a relay that never answers the
 * `markprice:` channel.
 */
export const MARK_WS_STALE_MS = 30_000;
const MARK_FALLBACK_REFRESH_MS = 15_000;

/**
 * Overlay polled REST marks onto the streamed ones for every instrument whose
 * stream is missing or stale. Exported for tests.
 */
export const mergeMarkFallback = (
  streamed: InstrumentToMark,
  lastStreamedAt: Record<string, number>,
  instrumentNames: string[],
  restMarkets: Pick<GetMarkets200Response, "instrument_name" | "mark_price">[] | undefined,
  now: number
): InstrumentToMark => {
  if (!restMarkets?.length) return streamed;
  let merged = streamed;
  instrumentNames.forEach((name) => {
    if (now - (lastStreamedAt[name] ?? 0) < MARK_WS_STALE_MS) return;
    const rest = restMarkets.find((m) => m.instrument_name === name);
    const mark = apiNum(rest?.mark_price as any);
    if (mark === undefined || mark <= 0) return;
    if (merged === streamed) merged = { ...streamed };
    merged[name] = { ...(streamed[name] ?? {}), mark_price: String(rest!.mark_price) };
  });
  return merged;
};

const getMarkPriceChannel = (tickers: IMultiMarkPriceAssetDerivative) =>
  Object.values(tickers).map(
    ({ asset, derivative }) =>
      `${WebsocketChannelEnum.MARK_PRICE}:${asset}:${derivative}`
  );

const useMarkPriceWSS = (tickers: IMultiMarkPriceAssetDerivative) => {
  const { triggerSubscribe, triggerUnsubscribe, lastMessages } =
    useContext(WebsocketContext);

  const [instrumentMark, setInstrumentMark] = useState<InstrumentToMark>({});
  const lastStreamedAt = useRef<Record<string, number>>({});
  const instrumentNames = useMemo(() => Object.keys(tickers), [tickers]);

  // One shared request for every mounted consumer (same SWR key).
  const { data: restMarkets } = useSWR<GetMarkets200Response[], AxiosError>(
    instrumentNames.length ? [APIEndpointEnum.MARKETS, "mark-fallback"] : undefined,
    async () => (await (await publicApi().getMarkets())()).data,
    { refreshInterval: MARK_FALLBACK_REFRESH_MS, dedupingInterval: 10_000 }
  );

  useEffect(() => {
    if (!Object.values(tickers).length) {
      return undefined;
    }

    const data = getMarkPriceChannel(tickers);
    triggerSubscribe("subscribe", data);
    return () => triggerUnsubscribe(data);
  }, [tickers, triggerSubscribe, triggerUnsubscribe]);

  // Receives messages and updates state
  useEffect(() => {
    if (!lastMessages || !Object.values(tickers).length) return;

    // Parsed once per frame, outside any state updater (FE-12), and applied
    // in one update per batch.
    const markPriceChannels = getMarkPriceChannel(tickers);
    let prices: InstrumentToMark | undefined;
    forEachFrame(lastMessages, "useMarkPriceWSS", (lastMessage) => {
      const frame = readFrame<IWSSMarkPriceResponse>(lastMessage);
      if (!frame) return;
      const { data, channel } = frame;
      if (!markPriceChannels.includes(channel) || !data?.prices?.length) return;

      data.prices.forEach((curr) => {
        lastStreamedAt.current[curr.instrument_name] = Date.now();
        prices = {
          ...(prices || {}),
          [curr.instrument_name]: {
            ...curr,
            // Omitted (or zero) stays undefined, never "0".
            mark_price: knownDisplayPrice(curr.mark_price),
          } as IWSSMark,
        };
      });
    });

    if (prices) {
      const next = prices;
      setInstrumentMark((prev) => ({ ...prev, ...next }));
    }
  }, [lastMessages, tickers]);

  // A stream that goes quiet changes no input above, and SWR keeps the same
  // `restMarkets` when a poll returns unchanged data — so re-check staleness on
  // a clock too, or the last streamed mark would be served forever.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!instrumentNames.length) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), MARK_FALLBACK_REFRESH_MS);
    return () => clearInterval(id);
  }, [instrumentNames.length]);

  const merged = useMemo(
    () =>
      mergeMarkFallback(
        instrumentMark,
        lastStreamedAt.current,
        instrumentNames,
        restMarkets,
        Date.now()
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [instrumentMark, instrumentNames, restMarkets, tick]
  );

  return {
    instrumentMark: merged,
  };
};

export default useMarkPriceWSS;
